import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, RefreshCw, Landmark, Factory, Target, Compass, Circle, Plus, Check } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import useSeo from "../hooks/useSeo";

/**
 * AdminBoosters — desk-side catalyst dashboard.
 *
 * Answers "what's the tape doing this week?" in one page:
 *   1. Macro events (FOMC / CPI / NFP / PCE) — impact-coloured
 *   2. Sectors we're currently covering, with open-alert counts + which
 *      of those alerts have earnings this week
 *   3. Every open alert with a catalyst in the next 7 days, sorted by
 *      earliest catalyst first
 *   4. Blindspots strip — sectors we DON'T cover today but should, with
 *      suggested tickers to add to the scanner universe
 *
 * Reads from `/api/admin/boosters` (admin-only).
 */
export default function AdminBoosters() {
  useSeo({
    title: "Boosters — Admin — Moonbucks",
    description: "Upcoming catalysts across the market, our sectors, and our alert board.",
    canonical: "https://moonbucks.io/admin/boosters",
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/boosters");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load boosters");
      toast.error("Boosters load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const impactColor = (impact) => {
    if (impact === "HIGH") return "text-red-500";
    if (impact === "MED") return "text-amber-500";
    return "text-emerald-500";
  };

  const fmtDate = (iso) => new Date(iso + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-8">
        <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-mb-emerald hover:underline mb-4" data-testid="admin-boosters-back">
          <ArrowLeft className="w-3 h-3" /> Back to admin cockpit
        </Link>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-3 mb-6">
          <div>
            <div className="label-uppercase text-mb-emerald">BOOSTERS</div>
            <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mt-1" data-testid="admin-boosters-title">
              Catalysts this week
            </h1>
            {data && (
              <p className="text-sm text-neutral-600 mt-1">
                Week of {fmtDate(data.week_range.start)} — {fmtDate(data.week_range.end)}
              </p>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-md" data-testid="admin-boosters-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Refresh
          </Button>
        </div>

        {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-300 text-sm text-red-700">{error}</div>}
        {!data && loading && <div className="text-neutral-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading catalysts…</div>}

        {data && (
          <>
            {/* Header tape summary */}
            <div className="bg-white border border-mb-border rounded-lg p-4 mb-6 grid grid-cols-3 divide-x divide-mb-border" data-testid="admin-boosters-summary">
              <Stat count={data.macro.length} label="Macro events" />
              <Stat count={data.sectors.reduce((n, s) => n + (s.symbols_with_earnings_this_week?.length || 0), 0)} label="Alert earnings this week" />
              <Stat count={data.alerts_with_catalysts.length} label="On our board · next 7d" />
            </div>

            {/* Macro */}
            <Section icon={Landmark} title="Macro — what moves SPY this week" testid="boosters-macro">
              {data.macro.length === 0 && <Empty>No scheduled macro events this week.</Empty>}
              {data.macro.map((e, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-mb-border/50 last:border-b-0 text-sm">
                  <Circle className={`w-2.5 h-2.5 flex-shrink-0 fill-current ${impactColor(e.impact)}`} />
                  <div className="w-24 font-mono-data text-neutral-500 flex-shrink-0">{fmtDate(e.date)}</div>
                  <div className="w-16 font-mono-data text-neutral-500 flex-shrink-0">{e.time_et}</div>
                  <div className="flex-1 text-neutral-800">{e.event}</div>
                  <div className={`text-[10px] font-mono-data font-bold ${impactColor(e.impact)}`}>{e.impact}</div>
                </div>
              ))}
            </Section>

            {/* Sectors */}
            <Section icon={Factory} title="Sectors in the spotlight" testid="boosters-sectors">
              {data.sectors.length === 0 && <Empty>No sectors covered yet.</Empty>}
              {data.sectors.map((s) => (
                <div key={s.sector} className="flex items-center gap-3 py-2 border-b border-mb-border/50 last:border-b-0 text-sm">
                  <div className="flex-1 font-display font-semibold">{s.sector}</div>
                  <div className="text-neutral-500 text-xs">{s.alerts_open} open</div>
                  <div className="flex-1 text-right">
                    {s.symbols_with_earnings_this_week.length > 0
                      ? <span className="font-mono-data text-mb-emerald">{s.symbols_with_earnings_this_week.join(", ")}</span>
                      : <span className="text-neutral-400 italic">no earnings this week</span>}
                  </div>
                </div>
              ))}
            </Section>

            {/* Alerts with catalysts */}
            <Section icon={Target} title="Alerts on our board with catalysts (next 7d)" testid="boosters-alerts">
              {data.alerts_with_catalysts.length === 0 && <Empty>No alerts have a catalyst inside the next 7 days.</Empty>}
              {data.alerts_with_catalysts.map((a) => (
                <div key={a.id} className="py-3 border-b border-mb-border/50 last:border-b-0">
                  <div className="flex items-center gap-3">
                    <div className="font-mono-data font-bold text-lg text-mb-ink w-16">{a.symbol}</div>
                    <div className="text-xs text-neutral-500 flex-1">
                      {a.sector} · {a.alert_class} · <span className="font-mono-data font-bold text-mb-emerald">{a.conviction_grade}</span>
                    </div>
                    <div className="text-sm text-neutral-700">
                      {a.catalyst_type} <span className="font-mono-data">{fmtDate(a.catalyst_date)}</span>
                    </div>
                    <div className={`text-xs font-mono-data w-24 text-right ${a.days_away <= 2 ? "text-red-500" : a.days_away <= 5 ? "text-amber-500" : "text-neutral-500"}`}>
                      {a.days_away === 0 ? "today" : a.days_away === 1 ? "tomorrow" : `in ${a.days_away}d`}
                    </div>
                  </div>
                  {a.entry_price && (
                    <div className="mt-1 pl-[76px] text-[11px] text-neutral-400 font-mono-data">
                      alert entry ${a.entry_price}
                    </div>
                  )}
                </div>
              ))}
            </Section>

            {/* Blindspots */}
            <Section icon={Compass} title="Blindspots — sectors we might expand into" testid="boosters-blindspots">
              {data.blindspots.map((b) => (
                <BlindspotRow key={b.name} blindspot={b} onAdded={load} />
              ))}
            </Section>

            <div className="text-[11px] text-neutral-400 mt-6">
              Macro calendar is hand-curated (updated quarterly). Earnings dates from yfinance,
              cached per-day. Computed {data.computed_at ? new Date(data.computed_at).toLocaleString() : ""}.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const Stat = ({ count, label }) => (
  <div className="text-center px-2">
    <div className="font-mono-data text-2xl font-bold text-mb-emerald">{count}</div>
    <div className="label-uppercase text-[10px] text-neutral-500 mt-0.5">{label}</div>
  </div>
);

const Section = ({ icon: Icon, title, testid, children }) => (
  <section className="mb-6" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-mb-emerald" />
      <h2 className="font-display font-semibold text-lg">{title}</h2>
    </div>
    <div className="bg-white border border-mb-border rounded-lg px-4">
      {children}
    </div>
  </section>
);

const Empty = ({ children }) => <div className="py-4 text-sm text-neutral-400 italic">{children}</div>;

// One row in the Blindspots section — inline "Add to scanner" button that
// posts every suggested ticker to `/admin/scanner/add-tickers` and reflects
// success/skip counts in a toast. Fires the parent `onAdded` to reload the
// dashboard so the row visibly moves from Blindspot → Sectors covered
// on the next scanner tick.
function BlindspotRow({ blindspot, onAdded }) {
  const [adding, setAdding] = useState(false);
  const [addedTickers, setAddedTickers] = useState(new Set());

  const addAll = async () => {
    setAdding(true);
    try {
      const r = await api.post("/admin/scanner/add-tickers", {
        symbols: blindspot.suggested_tickers,
        tier: "small",
        source: "blindspot",
      });
      const added = r.data.added || [];
      setAddedTickers(new Set(added));
      const parts = [
        added.length ? `Added ${added.length}` : null,
        r.data.skipped?.length ? `${r.data.skipped.length} already in universe` : null,
      ].filter(Boolean);
      toast.success(parts.join(" · ") || "No new tickers added");
      onAdded && onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add to scanner failed");
    } finally {
      setAdding(false);
    }
  };

  const allAdded = blindspot.suggested_tickers.every((t) => addedTickers.has(t));

  return (
    <div className="py-3 border-b border-mb-border/50 last:border-b-0" data-testid={`blindspot-${blindspot.name.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="font-display font-semibold text-mb-ink">{blindspot.name}</div>
        <div className="flex items-center gap-3">
          <div className="font-mono-data text-xs text-mb-emerald">{blindspot.suggested_tickers.join(" · ")}</div>
          <Button
            size="sm"
            variant={allAdded ? "outline" : "default"}
            onClick={addAll}
            disabled={adding || allAdded}
            className={`rounded-md ${allAdded ? "" : "bg-mb-emerald hover:bg-mb-emerald-dark text-white"}`}
            data-testid={`blindspot-add-btn-${blindspot.name.split(" ")[0].toLowerCase()}`}
          >
            {adding ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : allAdded ? <Check className="w-3 h-3 mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
            {allAdded ? "Added" : "Add to scanner"}
          </Button>
        </div>
      </div>
      <div className="text-xs text-neutral-500 mt-1">{blindspot.reason}</div>
    </div>
  );
}
